import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const IndustrySection = () => {
  const [activeIndustry, setActiveIndustry] = useState('technology');

  const industries = [
    {
      id: 'technology',
      name: 'Technology & Software',
      icon: 'Laptop',
      description: 'SaaS, fintech, and digital transformation companies',
      challenges: [
        'Intangible asset valuation',
        'Revenue recognition complexity',
        'High growth rate sustainability',
        'Intellectual property assessment'
      ],
      considerations: [
        'Recurring revenue models',
        'Customer acquisition costs',
        'Technology obsolescence risk',
        'Scalability factors'
      ],
      caseStudy: {
        title: 'Fintech Platform Valuation',
        description: 'Pre-Series B valuation for regulatory compliance and investor negotiations',
        outcome: 'Successfully supported $15M funding round with defensible valuation methodology'
      }
    },
    {
      id: 'manufacturing',
      name: 'Manufacturing & Industrial',
      icon: 'Factory',
      description: 'Traditional manufacturing, petrochemicals, and industrial services',
      challenges: [
        'Asset-heavy business models',
        'Cyclical market conditions',
        'Environmental liabilities',
        'Regulatory compliance costs'
      ],
      considerations: [
        'Replacement cost analysis',
        'Capacity utilization rates',
        'Supply chain dependencies',
        'ESG impact factors'
      ],
      caseStudy: {
        title: 'Petrochemical Company Acquisition',
        description: 'Fair value assessment for major acquisition in GCC market',
        outcome: 'Provided independent valuation supporting $120M transaction'
      }
    },
    {
      id: 'retail',
      name: 'Retail & Consumer',
      icon: 'ShoppingBag',
      description: 'Traditional retail, e-commerce, and consumer brands',
      challenges: [
        'Brand value quantification',
        'Omnichannel complexity',
        'Inventory management',
        'Consumer behavior shifts'
      ],
      considerations: [
        'Location premium analysis',
        'Digital transformation impact',
        'Customer loyalty metrics',
        'Market share dynamics'
      ],
      caseStudy: {
        title: 'Luxury Retail Chain',
        description: 'Family business succession planning and ownership restructuring',
        outcome: 'Facilitated smooth generational transition with tax-efficient structure'
      }
    },
    {
      id: 'healthcare',
      name: 'Healthcare & Life Sciences',
      icon: 'Heart',
      description: 'Medical devices, pharmaceuticals, and healthcare services',
      challenges: [
        'Regulatory approval risks',
        'R&D investment recovery',
        'Patent protection periods',
        'Reimbursement uncertainties'
      ],
      considerations: [
        'Clinical trial success rates',
        'Market access strategies',
        'Competitive landscape',
        'Healthcare policy impacts'
      ],
      caseStudy: {
        title: 'Medical Device Startup',
        description: 'Pre-IPO valuation for regulatory filing and investor communications',
        outcome: 'Supported successful public offering with credible valuation framework'
      }
    },
    {
      id: 'realestate',
      name: 'Real Estate & Construction',
      icon: 'Building2',
      description: 'Development, investment, and construction companies',
      challenges: [
        'Market cycle timing',
        'Development risk assessment',
        'Regulatory approval delays',
        'Financing structure complexity'
      ],
      considerations: [
        'Location and zoning factors',
        'Construction cost inflation',
        'Rental yield sustainability',
        'Exit strategy options'
      ],
      caseStudy: {
        title: 'Mixed-Use Development',
        description: 'Joint venture valuation for major urban development project',
        outcome: 'Enabled fair partnership structure for $200M development'
      }
    },
    {
      id: 'family',
      name: 'Family Businesses',
      icon: 'Users',
      description: 'Multi-generational businesses and family offices',
      challenges: [
        'Governance structure complexity',
        'Minority interest discounts',
        'Succession planning needs',
        'Liquidity constraints'
      ],
      considerations: [
        'Control premium analysis',
        'Family employment policies',
        'Dividend distribution patterns',
        'Next-generation involvement'
      ],
      caseStudy: {
        title: 'Trading Conglomerate',
        description: 'Third-generation succession planning and estate valuation',
        outcome: 'Structured tax-efficient transition preserving family control'
      }
    }
  ];

  const regionalFactors = [
    {
      title: 'IFRS Compliance',
      description:"IFRS financial reporting and business considerations",
      icon: 'BookOpen'
    },
    {
      title: 'Government Relations',
      description: 'Regulatory environment and government contract dependencies',
      icon: 'Building'
    },
    {
      title: 'Workforce',
      description: 'Labor market dynamics and dependency factors',
      icon: 'Globe'
    },
    {
      title: 'Oil Price Correlation',
      description: 'Economic cycle correlation with hydrocarbon markets',
      icon: 'TrendingUp'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-6">
            Industry Expertise
          </h2>
          <p className="text-xl text-text-secondary leading-relaxed">
            Deep sector knowledge combined with regional market understanding enables us to address industry-specific valuation challenges with precision and insight.
          </p>
        </div>

        {/* Industry Selection */}
        <div className="mb-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {industries?.map((industry) => (
              <button
                key={industry?.id}
                onClick={() => setActiveIndustry(industry?.id)}
                className={`p-4 rounded-xl border-2 transition-all duration-300 text-center ${
                  activeIndustry === industry?.id
                    ? 'border-accent bg-accent/5 shadow-brand'
                    : 'border-border bg-white hover:border-accent/50 hover:bg-accent/2'
                }`}
              >
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3 ${
                  activeIndustry === industry?.id ? 'bg-accent text-white' : 'bg-muted text-text-secondary'
                }`}>
                  <Icon name={industry?.icon} size={20} />
                </div>
                <h4 className="text-sm font-medium text-primary">{industry?.name}</h4>
              </button>
            ))}
          </div>

          {/* Active Industry Details */}
          <div className="bg-muted rounded-2xl p-8">
            {industries?.map((industry) => (
              activeIndustry === industry?.id && (
                <div key={industry?.id}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-accent rounded-xl flex items-center justify-center">
                      <Icon name={industry?.icon} size={28} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-primary">{industry?.name}</h3>
                      <p className="text-text-secondary">{industry?.description}</p>
                    </div>
                  </div>

                  <div className="grid lg:grid-cols-3 gap-8">
                    {/* Challenges */}
                    <div className="bg-white rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-primary mb-4 flex items-center">
                        <Icon name="AlertTriangle" size={20} className="text-warning mr-2" />
                        Key Challenges
                      </h4>
                      <ul className="space-y-3">
                        {industry?.challenges?.map((challenge, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <Icon name="Dot" size={12} className="text-warning flex-shrink-0 mt-2" />
                            <span className="text-sm text-text-primary">{challenge}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Considerations */}
                    <div className="bg-white rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-primary mb-4 flex items-center">
                        <Icon name="CheckCircle" size={20} className="text-success mr-2" />
                        Our Approach
                      </h4>
                      <ul className="space-y-3">
                        {industry?.considerations?.map((consideration, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <Icon name="Dot" size={12} className="text-success flex-shrink-0 mt-2" />
                            <span className="text-sm text-text-primary">{consideration}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Case Study */}
                    <div className="bg-white rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-primary mb-4 flex items-center">
                        <Icon name="FileText" size={20} className="text-accent mr-2" />
                        Case Study
                      </h4>
                      <div className="space-y-3">
                        <h5 className="font-medium text-primary">{industry?.caseStudy?.title}</h5>
                        <p className="text-sm text-text-secondary">{industry?.caseStudy?.description}</p>
                        <div className="pt-3 border-t border-border">
                          <p className="text-sm font-medium text-success">{industry?.caseStudy?.outcome}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>

        {/* Regional Considerations */}
        {/* <div>
          <h3 className="text-2xl font-bold text-primary mb-8 text-center">Market Considerations</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {regionalFactors?.map((factor, index) => (
              <div key={index} className="bg-muted rounded-xl p-6 text-center hover:shadow-brand transition-shadow duration-300">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name={factor?.icon} size={24} className="text-accent" />
                </div>
                <h4 className="text-lg font-semibold text-primary mb-3">{factor?.title}</h4>
                <p className="text-sm text-text-secondary">{factor?.description}</p>
              </div>
            ))}
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default IndustrySection;